import Splide from '@splidejs/splide';
import { Component } from '@theme/component';

class ApcProductHighlightSlider extends Component {
  #currentIndex = 0;
  #targetIndex = 0;
  #contentPanels = [];
  #contentTrack = null;
  #panelColors = [];
  #count = 0;
  #dots = [];
  /** @type {import('@splidejs/splide').Splide} */
  #splide = null;
  #cardTimer = null;
  #autoRotateMs = 0;
  #autoRotateTimer = null;
  #autoRotateFill = null;
  #autoRotateStartTime = 0;
  #autoRotateElapsed = 0;
  #isHovered = false;
  #resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.#syncContentHeight()));

  connectedCallback() {
    super.connectedCallback();

    const imageTrack = this.querySelector('.apc-product-highlight-slider__image-track');
    const contentTrack = this.querySelector('.apc-product-highlight-slider__content-track');
    if (!imageTrack || !contentTrack) return;

    this.#contentPanels = Array.from(contentTrack.querySelectorAll('.apc-product-highlight-slide'));
    this.#count = this.#contentPanels.length;
    this.#dots = Array.from(this.querySelectorAll('.apc-product-highlight-slider__dot'));

    // Panel backgrounds are transparent once JS is active — the track paints
    // the color and morphs it between slides for a clean transition
    this.#contentTrack = contentTrack;
    this.#panelColors = this.#contentPanels.map(
      (slide) =>
        slide.querySelector('.apc-product-highlight-slide__content')?.style.getPropertyValue('--slide-bg').trim() || '',
    );
    this.#setTrackColor(0);
    this.#resizeObserver.observe(this);

    const list = imageTrack.querySelector('.splide__list');

    // Extract each slide's media (+ hotspots) into the Splide list
    this.#contentPanels.forEach((slide) => {
      const media = slide.querySelector('.apc-product-highlight-slide__media');
      const hotspots = Array.from(slide.querySelectorAll('apc-phslider-hotspot'));

      const li = document.createElement('li');
      li.className = 'splide__slide apc-product-highlight-slider__image-slide';

      if (media) li.appendChild(media);
      hotspots.forEach((h) => li.appendChild(h));

      list.appendChild(li);
    });

    // Assign stagger index + total to the actual content rows. Blocks are
    // usually nested inside a layout group, so a panel's only child is the
    // `.group-block` wrapper — drill through single-group wrappers so the
    // stagger lands on the visible rows, not the group.
    this.#contentPanels.forEach((slide) => {
      const content = slide.querySelector('.apc-product-highlight-slide__content');
      if (!content) return;
      const items = this.#getStaggerItems(content);
      const total = items.length;
      items.forEach((item, i) => {
        item.classList.add('apc-product-highlight-slide__item');
        item.style.setProperty('--i', i);
        item.style.setProperty('--total', total);
      });
    });

    this.setAttribute('data-js', '');

    // Double rAF: the hidden base state must paint first so the
    // first slide's staggered enter animation plays on load
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#contentPanels[0]?.setAttribute('data-active', '');
        this.#syncContentHeight();
      });
    });

    this.#autoRotateMs = parseInt(this.dataset.autoRotate, 10) || 0;
    if (this.#autoRotateMs > 0) {
      this.style.setProperty('--apc-rotate-duration', `${this.#autoRotateMs}ms`);
    }

    this.#splide = new Splide(imageTrack, {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      flickMaxPages: 1,
      // Lower flick momentum + higher drag threshold so one swipe = one slide
      // (default flickPower 600 lets a fast flick carry past the next slide).
      flickPower: 200,
      dragMinThreshold: 20,
      arrows: false,
      pagination: false,
      gap: 0,
      padding: 0,
      speed: 400,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    });

    // Kick off leave animation + crossfade background of incoming panel
    this.#splide.on('move', (newIndex) => {
      // Guard: if moving to the same index (e.g. drag that will snap back), do nothing
      if (newIndex === this.#currentIndex) return;

      this.#targetIndex = newIndex;
      clearTimeout(this.#cardTimer);
      this.#hideHotspotCards(this.#currentIndex);
      this.#setTrackColor(newIndex);
      this.#syncContentHeight(newIndex);

      const prev = this.#contentPanels[this.#currentIndex];
      prev?.removeAttribute('data-active');
      prev?.setAttribute('data-leaving', '');
      this.#contentPanels[newIndex]?.setAttribute('data-entering', '');
    });

    // After image has slid, promote incoming panel to active
    this.#splide.on('moved', (newIndex) => {
      const prev = this.#contentPanels[this.#currentIndex];

      // Snap-back: drag didn't complete, slider returned to current slide
      if (newIndex === this.#currentIndex) {
        prev?.removeAttribute('data-leaving');
        prev?.setAttribute('data-active', '');
        this.#contentPanels[this.#targetIndex]?.removeAttribute('data-entering');
        this.#setTrackColor(this.#currentIndex);
        this.#onSlideSettled();
        return;
      }

      this.#currentIndex = newIndex;
      const next = this.#contentPanels[newIndex];

      prev?.removeAttribute('data-leaving');
      next?.removeAttribute('data-entering');
      next?.setAttribute('data-active', '');

      this.#updateDots(newIndex);
      this.#syncContentHeight(newIndex);

      // Show hotspot cards after the hotspot entrance animation completes
      this.#cardTimer = setTimeout(() => this.#showHotspotCards(newIndex), 800);

      this.#onSlideSettled();
    });

    this.#splide.mount();

    // Auto-show cards on the first slide
    this.#cardTimer = setTimeout(() => this.#showHotspotCards(0), 800);

    this.#dots.forEach((dot) => {
      dot.addEventListener('click', () => this.#splide.go(Number(dot.dataset.index)));
    });

    if (this.#autoRotateMs > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.addEventListener('mouseenter', () => {
        this.#isHovered = true;
        this.#pauseAutoRotate();
      });
      this.addEventListener('mouseleave', () => {
        this.#isHovered = false;
        this.#resumeAutoRotate();
      });
      this.#startAutoRotate();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#cardTimer);
    clearTimeout(this.#autoRotateTimer);
    this.#resizeObserver.disconnect();
    this.#splide?.destroy(true);
  }

  /**
   * Goes back to the previous slide. Invoked by the Navigation block's
   * arrow buttons via the declarative `on:click="/previous"` wiring.
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.#splide?.go('<');
  }

  /**
   * Advances to the next slide. Invoked by the Navigation block's arrow
   * buttons via the declarative `on:click="/next"` wiring.
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.#splide?.go('>');
  }

  /**
   * Resolve the elements to stagger inside a content panel. Blocks are often
   * nested in a layout group, so a panel may contain a single `.group-block`
   * wrapper. Drill through single-group wrappers to reach the row of items.
   * @param {HTMLElement} content
   * @returns {HTMLElement[]}
   */
  #getStaggerItems(content) {
    let container = content;
    while (true) {
      const children = Array.from(container.children);
      if (children.length !== 1 || !children[0].classList.contains('group-block')) break;
      const inner = children[0].querySelector(':scope > .group-block__content');
      if (!inner) break;
      container = inner;
    }
    return Array.from(container.children);
  }

  /**
   * Keeps the desktop content track large enough for the active panel. Panels
   * are absolutely positioned for the transition, so their intrinsic height
   * would otherwise not contribute to the grid row.
   * @param {number} [index]
   */
  #syncContentHeight(index = this.#currentIndex) {
    const content = this.#contentPanels[index]?.querySelector('.apc-product-highlight-slide__content');
    if (!(content instanceof HTMLElement) || !this.#contentTrack) return;

    // `min-height: 100%` keeps the panel level with the track after it has
    // been measured. Remove it briefly to capture the content's natural
    // height, which also lets the track shrink for a shorter subsequent slide.
    const minHeight = content.style.minHeight;
    content.style.minHeight = '0';
    const height = `${content.scrollHeight}px`;
    content.style.minHeight = minHeight;

    if (this.#contentTrack.style.getPropertyValue('--apc-content-height') !== height) {
      this.#contentTrack.style.setProperty('--apc-content-height', height);
    }
  }

  #onSlideSettled() {
    if (!this.#isHovered) {
      this.#startAutoRotate();
    } else if (this.#autoRotateMs > 0) {
      // Update the fill visual on the new dot but keep it paused
      this.#restartProgress();
      this.#autoRotateElapsed = 0;
      if (this.#autoRotateFill) this.#autoRotateFill.style.setProperty('--progress-play-state', 'paused');
    }
  }

  #startAutoRotate() {
    if (this.#autoRotateMs <= 0) return;
    clearTimeout(this.#autoRotateTimer);
    this.#autoRotateElapsed = 0;
    this.#autoRotateStartTime = Date.now();
    this.#restartProgress();
    this.#autoRotateTimer = setTimeout(() => this.#splide.go('>'), this.#autoRotateMs);
  }

  #pauseAutoRotate() {
    clearTimeout(this.#autoRotateTimer);
    this.#autoRotateElapsed += Date.now() - this.#autoRotateStartTime;
    if (this.#autoRotateFill) this.#autoRotateFill.style.setProperty('--progress-play-state', 'paused');
  }

  #resumeAutoRotate() {
    if (this.#autoRotateMs <= 0) return;
    const remaining = Math.max(0, this.#autoRotateMs - this.#autoRotateElapsed);
    this.#autoRotateStartTime = Date.now();
    if (this.#autoRotateFill) this.#autoRotateFill.style.removeProperty('--progress-play-state');
    this.#autoRotateTimer = setTimeout(() => this.#splide.go('>'), remaining);
  }

  #restartProgress() {
    // Clear all dot fills
    this.#dots.forEach((d) => {
      const f = d.querySelector('.apc-product-highlight-slider__dot-fill');
      if (f) {
        f.classList.remove('is-animating');
        f.style.removeProperty('--progress-play-state');
      }
    });
    // Animate the active dot's fill
    const activeDot = this.#dots[this.#currentIndex];
    this.#autoRotateFill = activeDot?.querySelector('.apc-product-highlight-slider__dot-fill') ?? null;
    if (!this.#autoRotateFill) return;
    void this.#autoRotateFill.offsetWidth; // force reflow to restart animation
    this.#autoRotateFill.classList.add('is-animating');
  }

  #setTrackColor(index) {
    const color = this.#panelColors[index];
    if (color) this.#contentTrack.style.backgroundColor = color;
  }

  #showHotspotCards(index) {
    const slide = this.#splide?.Components.Slides.getAt(index)?.slide;
    slide?.querySelectorAll('apc-phslider-hotspot').forEach((hotspot) => hotspot.open());
  }

  #hideHotspotCards(index) {
    const slide = this.#splide?.Components.Slides.getAt(index)?.slide;
    slide?.querySelectorAll('apc-phslider-hotspot').forEach((hotspot) => hotspot.close());
  }

  #updateDots(index) {
    this.#dots.forEach((dot) => {
      const active = Number(dot.dataset.index) === index;
      dot.classList.toggle('apc-product-highlight-slider__dot--active', active);
      dot.setAttribute('aria-selected', String(active));
    });
  }
}

class ApcPhsliderHotspot extends Component {
  #dot = null;
  #card = null;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    // The parent slider relocates hotspots into the Splide list (see its connectedCallback), so
    // this element connects more than once. Without teardown each reconnect stacked another click
    // listener, making the toggle fire twice (open + close) per tap.
    this.#abortController = new AbortController();

    // querySelector rather than refs: the dot/card markup is conditional in the liquid, and
    // requiredRefs would throw on hotspots rendered without a product.
    this.#dot = this.querySelector('.apc-phslider-hotspot__dot');
    this.#card = this.querySelector('.apc-phslider-hotspot__card');

    if (!this.#dot || !this.#card) return;

    this.#dot.addEventListener('click', () => this.#toggle(), { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  open() {
    if (!this.#dot || !this.#card) return;
    this.#dot.setAttribute('aria-expanded', 'true');
    // Reveal first so the card has real dimensions to measure; #alignCard runs
    // synchronously, so the browser paints once with the final placement.
    this.#card.hidden = false;
    this.#alignCard();
  }

  close() {
    if (!this.#dot || !this.#card) return;
    this.#dot.setAttribute('aria-expanded', 'false');
    this.#card.hidden = true;
  }

  #toggle() {
    const isOpen = this.#dot.getAttribute('aria-expanded') === 'true';
    isOpen ? this.close() : this.open();
  }

  #alignCard() {
    const container = this.closest('.apc-product-highlight-slider__image-slide');
    if (!container) return;

    // Horizontal: compute where the centered card would land using --hotspot-x
    // and the container's pixel width, then pin to an edge if it would overflow.
    const hotspotX = parseFloat(this.style.getPropertyValue('--hotspot-x')) || 50;
    const containerWidth = container.offsetWidth;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const cardMinWidth = 16 * rem; // min-width: 16rem

    const buttonCenter = (hotspotX / 100) * containerWidth;
    const idealLeft = buttonCenter - cardMinWidth / 2;

    delete this.dataset.cardAlign;
    if (idealLeft < 0) {
      this.dataset.cardAlign = 'left';
    } else if (idealLeft + cardMinWidth > containerWidth) {
      this.dataset.cardAlign = 'right';
    }

    // Vertical: the card sits above the dot by default. If that placement clips
    // past the top of the slide, flip it below the dot (arrow then points up).
    delete this.dataset.cardPosition;
    const containerTop = container.getBoundingClientRect().top;
    const cardTop = this.#card.getBoundingClientRect().top;
    if (cardTop < containerTop) {
      this.dataset.cardPosition = 'below';
    }
  }
}

if (!customElements.get('apc-product-highlight-slider')) {
  customElements.define('apc-product-highlight-slider', ApcProductHighlightSlider);
}

if (!customElements.get('apc-phslider-hotspot')) {
  customElements.define('apc-phslider-hotspot', ApcPhsliderHotspot);
}
